import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryReturnType
} from '@tanstack/vue-query';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import type { DefaultError } from '@tanstack/query-core';
import type { IListMessagesResponse } from '@/types/services/message.service';
import axiosInstance from '@/plugins/axios';
import type { ComputedRef, Reactive } from 'vue';
import type {
  IDeleteRecentSearchRequest,
  IRecentSearchResponse,
  ISearchKeywordsResponse,
  ISearchParams
} from '@/types/services/search.service';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import useSnackbar from '@/composables/useSnackbar';
import { isAxiosError } from 'axios';

/**
 * This service composable is used for all draft related requests
 * All of these requests require the user to be authenticated, so they should use the userIndex
 * and queriesEnabled variables for proper request control
 */
const useSearchService = () => {
  const { userIndex, queriesEnabled } = useAuthenticatedQuery();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { addSnackbar } = useSnackbar();

  const { mobile } = useDisplay();

  const searchMessages = (
    isSearchView: ComputedRef<boolean>,
    searchValue: ComputedRef<string>,
    params: Reactive<ISearchParams>,
    isAllResultsActionRunning: ComputedRef<boolean>
  ): UseQueryReturnType<IListMessagesResponse | null, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'search', 'query', searchValue, params],
      queryFn: async ({ signal }) => {
        if (!searchValue.value) {
          return null;
        }
        return (
          await axiosInstance.get(`/u/${userIndex.value}/search`, {
            params: {
              search: searchValue.value,
              setRecent: true,
              ...params
            },
            signal
          })
        ).data;
      },
      retry: 1,
      enabled: () =>
        isSearchView.value &&
        searchValue.value.length > 0 &&
        queriesEnabled.value &&
        !isAllResultsActionRunning.value,
      placeholderData: (prevData) => (mobile.value ? prevData : undefined)
    });
  };

  const searchKeywords = (
    extendedSearchDisplayed: ComputedRef<boolean>
  ): UseQueryReturnType<ISearchKeywordsResponse, DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'search', 'keywords'],
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get(`/u/${userIndex.value}/search/keywords`, {
            signal
          })
        ).data;
      },
      enabled: () => queriesEnabled.value && extendedSearchDisplayed.value
    });
  };

  const recentSearches = (
    extendedSearchDisplayed: ComputedRef<boolean>
  ): UseQueryReturnType<IRecentSearchResponse[], DefaultError> => {
    return useQuery({
      queryKey: ['authenticatedQuery', userIndex, 'search', 'recentSearches'],
      queryFn: async ({ signal }) => {
        return (
          await axiosInstance.get<IRecentSearchResponse>(
            `/u/${userIndex.value}/search/recentSearches`,
            {
              signal
            }
          )
        ).data;
      },
      enabled: () => queriesEnabled.value && extendedSearchDisplayed.value
    });
  };

  const deleteRecentSearch = () => {
    return useMutation<void, DefaultError, IDeleteRecentSearchRequest>({
      mutationKey: ['authenticatedMutation', userIndex, 'search', 'deleteRecentSearch'],
      mutationFn: async ({ id }) => {
        await axiosInstance
          .delete(`/u/${userIndex.value}/search/recentSearches/${id}`)
          .catch((error) => {
            // Quietly fail 404 and update the list
            if (isAxiosError(error) && error.response?.status === 404) {
              queryClient.setQueryData<IRecentSearchResponse[]>(
                ['authenticatedQuery', userIndex, 'search', 'recentSearches'],
                (oldData) => {
                  if (!oldData) {
                    return oldData;
                  }
                  return oldData.filter((search) => search.id !== id);
                }
              );
              return;
            }

            throw error;
          });
      },
      onSuccess: () => {
        addSnackbar({
          message: t('search.recentSearchDeleted'),
          type: 'success'
        });
      }
    });
  };

  return { searchMessages, searchKeywords, recentSearches, deleteRecentSearch };
};

export default useSearchService;
